import {
  differenceInWeeks,
  isBefore,
  parse,
  getDay,
  isThisWeek,
  addWeeks,
  isSameWeek,
  isToday,
  isTomorrow,
  format,
  isFuture,
} from 'date-fns'

import { downloadStringAsFile } from 'Utils/Misc'
import { escapeCsvString } from 'shared/csv'
import { dropTimeZoneFromDate } from 'shared/libraries/utils/date'
import { days } from '../new/PickupHeader/const'
import { dateTimeFormat } from './constants'

const getFlyerPacksByAmbassadorAndCampaign = (
  ambassadorId,
  campaignId,
  flyerPacks,
) => {
  return flyerPacks.filter(
    flyerPack =>
      flyerPack.ambassadorId === ambassadorId &&
      flyerPack.campaignId === campaignId,
  )
}

const parseMissionData = (mission, givenOutCodes) => {
  return {
    userId: mission.userId,
    name: mission.AMBASSADOR,
    phoneNumber: mission['ambassadorMobilePhone'],
    email: mission['ambassadorEmail'],
    campaigns: [
      {
        campaignId: mission.campaignId,
        campaignName: mission['CAMPAIGN NAME'],
        missions: [mission],
        flyerPacks: getFlyerPacksByAmbassadorAndCampaign(
          mission.userId,
          mission.campaignId,
          givenOutCodes,
        ),
      },
    ],
  }
}

const addNextDatesToCampaigns = (parsedDataWithDates, brandAmbassadorData) => {
  const campaignsWithDates = brandAmbassadorData.campaigns.map(campaign => {
    let nextPickupDate
    let nextMissionDate
    const { missions } = campaign
    for (const mission of missions) {
      const now = new Date()
      if (
        mission['SCHEDULED PICKUP TIME'] &&
        !isBefore(mission['SCHEDULED PICKUP TIME'], now)
      ) {
        if (!nextPickupDate) {
          nextPickupDate = mission['SCHEDULED PICKUP TIME']
        } else if (
          !!nextPickupDate &&
          isBefore(mission['SCHEDULED PICKUP TIME'], nextPickupDate)
        ) {
          nextPickupDate = mission['SCHEDULED PICKUP TIME']
        }
      }

      if (mission.dateTime) {
        if (!nextMissionDate) {
          nextMissionDate = mission.dateTime
        } else if (isBefore(mission.dateTime, nextMissionDate)) {
          nextMissionDate = mission.dateTime
        }
      }
    }
    return { ...campaign, nextMissionDate, nextPickupDate }
  })
  return [
    ...parsedDataWithDates,
    { ...brandAmbassadorData, campaigns: campaignsWithDates },
  ]
}

const addCalculatedDataToAmbassadors = (
  parsedDataWithDates,
  brandAmbassadorData,
) => {
  let nextPickupDate
  let nextMissionDate
  let flyerPacksMissing = false
  let totalMissions = 0
  let totalFlyerPacks = 0
  const { campaigns } = brandAmbassadorData
  for (const campaign of campaigns) {
    const campaignTotalMissions = (campaign.missions || []).length
    const campaignTotalFlyerPacks = (campaign.flyerPacks || []).length
    const now = new Date()
    if (campaign.nextPickupDate && !isBefore(campaign.nextPickupDate, now)) {
      if (!nextPickupDate) {
        nextPickupDate = campaign.nextPickupDate
      } else if (
        !!nextPickupDate &&
        isBefore(campaign.nextPickupDate, nextPickupDate)
      ) {
        nextPickupDate = campaign.nextPickupDate
      }
    }
    if (campaign.nextMissionDate) {
      if (!nextMissionDate) {
        nextMissionDate = campaign.nextMissionDate
      } else if (isBefore(campaign.nextMissionDate, nextMissionDate)) {
        nextMissionDate = campaign.nextMissionDate
      }
    }
    if (campaignTotalMissions > campaignTotalFlyerPacks) {
      flyerPacksMissing = true
    }
    totalMissions += campaignTotalMissions
    totalFlyerPacks += campaignTotalFlyerPacks
  }

  return [
    ...parsedDataWithDates,
    {
      ...brandAmbassadorData,
      nextPickupDate,
      nextMissionDate,
      flyerPacksMissing,
      totalMissions,
      totalFlyerPacks,
    },
  ]
}

export const parseBriefTableData = (missions, givenOutCodes) => {
  const now = new Date()
  const parsedData =
    missions && Array.isArray(missions)
      ? missions
          .reduce((parsedMissions, mission) => {
            const missionDate = mission.dateTime
              ? parse(dropTimeZoneFromDate(mission.dateTime))
              : null
            const weeksDiff = missionDate
              ? differenceInWeeks(missionDate, now)
              : -1
            const isFuturePickup = missionDate ? isFuture(missionDate) : false
            if (weeksDiff >= 0 && weeksDiff <= 1 && isFuturePickup) {
              const currentAmbassador = parsedMissions.find(
                m => m.userId === mission.userId,
              )
              if (currentAmbassador !== undefined) {
                const currentCampaign = currentAmbassador.campaigns.find(
                  c => c.campaignId === mission.campaignId,
                )
                if (currentCampaign !== undefined) {
                  currentCampaign.missions.push(mission)
                } else {
                  currentAmbassador.campaigns.push({
                    campaignId: mission.campaignId,
                    campaignName: mission['CAMPAIGN NAME'],
                    missions: [mission],
                    flyerPacks: getFlyerPacksByAmbassadorAndCampaign(
                      mission.userId,
                      mission.campaignId,
                      givenOutCodes,
                    ),
                  })
                }
              } else {
                parsedMissions.push(parseMissionData(mission, givenOutCodes))
              }
            }
            return parsedMissions
          }, [])
          .reduce(addNextDatesToCampaigns, [])
          .reduce(addCalculatedDataToAmbassadors, [])
          .sort((a, b) =>
            genericSort(parse(a.nextPickupDate), parse(b.nextPickupDate)),
          )
      : []
  parsedData.forEach(m => {
    m.campaigns.sort((a, b) =>
      genericSort(parse(a.nextPickupDate), parse(b.nextPickupDate)),
    )
  })
  return parsedData
}

export const getFilteredBriefRows = (
  rows,
  selectedBrandAmbassadors,
  selectedCampaigns,
  selectedDays,
  selectedWeeks,
  selectedPreset,
) => {
  const now = new Date()

  return rows.filter(m => {
    let baMatch = true
    let daysMatch = true
    let weeksMatch = true
    let presetMatch = true
    let campaignMatch = false

    const nextPickupDate = m.nextPickupDate
      ? parse(dropTimeZoneFromDate(m.nextPickupDate))
      : undefined

    if (selectedBrandAmbassadors && selectedBrandAmbassadors.length) {
      baMatch =
        selectedBrandAmbassadors.find(ba => ba.id === m.id) !== undefined
    }

    if (selectedDays && selectedDays.length) {
      if (nextPickupDate) {
        const dayNumber = getDay(nextPickupDate)
        const dayObj = days.find(d => d.number === dayNumber)
        if (dayObj !== undefined) {
          daysMatch = selectedDays.find(d => d === dayObj.name) !== undefined
        }
      } else {
        daysMatch = false
      }
    }

    if (selectedCampaigns && selectedCampaigns.length) {
      for (const campaign of m.campaigns) {
        if (
          selectedCampaigns.find(sc => sc.name === campaign.campaignName) !==
          undefined
        ) {
          campaignMatch = true
          break
        }
      }
    } else {
      campaignMatch = true
    }

    if (selectedWeeks && selectedWeeks.length) {
      if (nextPickupDate) {
        let isCurrentWeek = false
        if (selectedWeeks.includes('Current Week')) {
          isCurrentWeek = isThisWeek(nextPickupDate)
        }

        let isNextWeek = false
        if (selectedWeeks.includes('Next Week')) {
          const datePlusOneWeek = addWeeks(now, 1)
          isNextWeek = isSameWeek(nextPickupDate, datePlusOneWeek)
        }

        weeksMatch = isCurrentWeek || isNextWeek
      } else {
        weeksMatch = false
      }
    }

    if (selectedPreset === 1) {
      if (nextPickupDate) {
        presetMatch = isToday(nextPickupDate)
      } else {
        presetMatch = false
      }
    }

    if (selectedPreset === 2) {
      if (nextPickupDate) {
        presetMatch = isTomorrow(nextPickupDate)
      } else {
        presetMatch = false
      }
    }

    return baMatch && daysMatch && weeksMatch && presetMatch && campaignMatch
  })
}

export const downloadPickupBriefCSV = brief => {
  let csvContent =
    'NAME,PHONE NUMBER,EMAIL,NEXT PICKUP,NEXT MISSION,MISSIONS BOOKED & ONGOING,FLYER PACKS\n'
  brief.forEach(
    ({
      name,
      phoneNumber,
      email,
      nextPickupDate,
      nextMissionDate,
      totalMissions,
      totalFlyers,
    }) => {
      csvContent +=
        [
          escapeCsvString(name || ''),
          escapeCsvString(phoneNumber || ''),
          escapeCsvString(email || ''),
          escapeCsvString(
            nextPickupDate
              ? format(dropTimeZoneFromDate(nextPickupDate), dateTimeFormat)
              : '',
          ),
          escapeCsvString(
            nextMissionDate
              ? format(dropTimeZoneFromDate(nextMissionDate), dateTimeFormat)
              : '',
          ),
          escapeCsvString(totalMissions || 0),
          escapeCsvString(totalFlyers || 0),
        ].join(',') + '\n'
    },
  )
  downloadStringAsFile(csvContent, `pickup-brief.csv`)
}

export const genericSort = (a, b) => (a !== b ? (a < b ? -1 : 1) : 0)

export const sortPickupBriefRows = (type, rows) => {
  let orderedRows = rows
  if (type === 'name') {
    orderedRows = rows.sort((a, b) =>
      genericSort(a.name.toLowerCase(), b.name.toLowerCase()),
    )
  } else if (type === 'next pickup') {
    orderedRows.sort((a, b) =>
      genericSort(parse(a.nextPickupDate), parse(b.nextPickupDate)),
    )
    orderedRows.forEach(row => {
      row.campaigns.sort((a, b) =>
        genericSort(parse(a.nextPickupDate), parse(b.nextPickupDate)),
      )
    })
  } else if (type === 'next mission') {
    orderedRows = rows.sort((a, b) =>
      genericSort(parse(a.nextMissionDate), parse(b.nextMissionDate)),
    )
  } else if (type.includes('missions booked')) {
    orderedRows = rows.sort((a, b) =>
      genericSort(a.totalMissions, b.totalMissions),
    )
  } else if (type === 'flyer packs') {
    orderedRows = rows.sort((a, b) => genericSort(a.totalFlyers, b.totalFlyers))
  }

  return orderedRows
}
